import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "../../assets/css/article/ArticlePhotosViewer.css";
import useArticles from "../../hooks/useArticle";
import { imageArticleUrl } from "../../utils/modules";
import defaultAnnonce from "../../assets/images/articles/no-photo.png";

export default function ArticlePhotosViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { findOneArticle } = useArticles();

  const [article, setArticle] = useState(null);
  const [active, setActive] = useState(0);

  // ✅ Si on vient du détail: on reçoit images + startIndex
  const stateImages = location.state?.images || null;
  const startIndex = Number(location.state?.startIndex || 0);

  const formatPhotoPath = (p) => (p || "").replace(/\\/g, "/");

  const buildPhotoUrl = (storedPath) => {
    const p = formatPhotoPath(storedPath);
    if (!p) return "";
    if (p.startsWith("http://") || p.startsWith("https://")) return p;

    // Tes photos semblent stockées avec "files/..."
    const idx = p.indexOf("files/");
    const relative = idx !== -1 ? p.slice(idx) : p;
    return `${imageArticleUrl}/${relative}`;
  };

  // ✅ close viewer => revient au détail (ou back)
  const closeViewer = () => {
    if (location.state?.from) return navigate(location.state.from);
    return navigate(-1);
  };

  // ✅ Si stateImages est présent => pas besoin de refetch
  useEffect(() => {
    let mounted = true;

    if (stateImages?.length) {
      const clamped = Math.min(Math.max(startIndex, 0), stateImages.length - 1);
      setActive(clamped);
      return () => {
        mounted = false;
      };
    }

    // Sinon: refetch article
    if (!id) return () => {
      mounted = false;
    };

    findOneArticle(id)
      .then((res) => {
        if (!mounted) return;
        const data = res?.data?.data || null;
        setArticle(data);
      })
      .catch(() => {
        if (!mounted) return;
        setArticle(null);
      });

    return () => {
      mounted = false;
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ images finales (state > article.photos)
  const images = useMemo(() => {
    if (stateImages?.length) return stateImages;

    const pics = article?.photos || [];
    return pics.map(buildPhotoUrl).filter(Boolean);
  }, [stateImages, article]);

  // ✅ recaler active si images change
  useEffect(() => {
    if (!images?.length) return;
    setActive((prev) => Math.min(prev, images.length - 1));
  }, [images?.length]);

  const goPrev = () => {
    if (!images?.length) return;
    setActive((p) => (p - 1 + images.length) % images.length);
  };

  const goNext = () => {
    if (!images?.length) return;
    setActive((p) => (p + 1) % images.length);
  };

  // ✅ navigation clavier (optionnel, mais utile)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeViewer();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [images]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="container utilisateur_compte_container lbv_container">
      {/* ✅ Croix identique à UtilisateurCompte */}
      <i
        className="bi bi-x-circle login_icon_x"
        role="button"
         onClick={() => navigate("/article/details/" + id)}
        aria-label="Fermer"
        title="Fermer"
      ></i>

      {/* Header style UtilisateurCompte */}
      <div className="utilisateur_compte_header lbv_header">
        <h1>Photos</h1>
        <p className="text-uppercase">
          {images?.length ? `${active + 1}/${images.length}` : "Aucune photo"}
        </p>
      </div>

      {/* Image centrée */}
      <div className="lbv_stage">
        <button
          type="button"
          className="lbv_nav lbv_nav_left"
          onClick={goPrev}
          aria-label="Image précédente"
          disabled={!images?.length}
        >
          <i className="bi bi-chevron-left"></i>
        </button>

        <div className="lbv_main">
          <img
            src={images?.[active] || defaultAnnonce}
            alt={`Image ${active + 1}`}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = defaultAnnonce;
            }}
          />
        </div>

        <button
          type="button"
          className="lbv_nav lbv_nav_right"
          onClick={goNext}
          aria-label="Image suivante"
          disabled={!images?.length}
        >
          <i className="bi bi-chevron-right"></i>
        </button>
      </div>

      {/* Miniatures */}
      <div className="lbv_thumbs_wrap">
        <div className="lbv_thumbs_title utilisateur_compte_menu_item">
          <div className="lbv_thumbs_title_left">
            <i className="bi bi-images"></i>
            <span>Galerie</span>
          </div>

          <span className="utilisateur_compte_notification">
            {images?.length || 0}
          </span>
        </div>

        <div className="lbv_thumbs">
          {(images || []).map((src, idx) => (
            <button
              key={src + idx}
              type="button"
              className={`lbv_thumb ${idx === active ? "active" : ""}`}
              onClick={() => setActive(idx)}
              aria-label={`Voir l'image ${idx + 1}`}
              title={`Image ${idx + 1}`}
            >
              <img
                src={src}
                alt={`Miniature ${idx + 1}`}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = defaultAnnonce;
                }}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
